import React, { useCallback, useEffect } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  Divider,
  Tab,
  TabTitleText,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-mobx-router5';

import useMobxStores from 'hooks/useMobxStores';
import AvailableLanguages from 'components/Course/OverviewTab/AvailableLanguages';
import Prerequisites from 'components/Course/OverviewTab/Prerequisites';
import SidebarDetails from 'components/Course/OverviewTab/SidebarDetails';
import Description from 'components/Course/OverviewTab/Description';
import { LandingLayout } from 'components/Landing/LandingLayout';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import { extractFromSlug } from 'helpers/utils';

import VersionsDropdown from 'components/Versions/Dropdown';
import TechOverviewOverviewStore from './store';

import './styles.scss';

const TechOverviewOverview = () => {
  const { t } = useTranslation();
  const { routerStore, progressStore, vocabularyStore, catalogStore, uiStore } =
    useMobxStores();

  const { route } = routerStore;
  const { slug: techOverviewSlug } = (route as any).params;

  const store = useLocalStore(
    () =>
      new TechOverviewOverviewStore(
        techOverviewSlug,
        progressStore,
        vocabularyStore,
        catalogStore,
        uiStore,
      ),
  );

  const {
    audienceLabels,
    currentVersion,
    description,
    isLoading,
    prerequisites,
    productLabels,
    progress,
    title,
    topicLabels,
    translations,
    versions,
  } = store;

  const { code } = extractFromSlug(techOverviewSlug);

  const handleSelect = useCallback(
    (value: string | number | undefined) => {
      routerStore.navigate('tech-overview:overview', {
        slug: `${code}-${value}`,
      });
    },
    [code, routerStore],
  );

  useEffect(() => {
    store.fetchOverviewData(techOverviewSlug, 'en-US');
  }, [techOverviewSlug]);

  const launchButtonLabel =
    progress?.total_progress > 0 ? t('Continue') : t('Launch');

  const formattedProgress = (progress?.total_progress * 100).toFixed();

  return (
    <div className="container-grid-responsive">
      <div className="overview">
        {!isLoading && (
          <>
            <Helmet title={`${title} | ${t('Overview')} - Red Hat Learning`} />
            <div className="overview__breadcrumb">
              <Breadcrumb>
                <BreadcrumbItem to="catalog">
                  <Link
                    href
                    routeName="catalog"
                    routeParams={catalogStore.lastCatalogParams}
                  >
                    {t('Catalog')}
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbItem isActive>{title}</BreadcrumbItem>
              </Breadcrumb>
            </div>
            <div className="overview__header">
              <h1>{title}</h1>
              <Divider component="div" className="overview__divider" />
            </div>
            <div className="overview__content">
              <div className="overview__main">
                <LandingLayout.Tabs mountOnEnter defaultActiveKey={0}>
                  <Tab
                    eventKey={0}
                    title={<TabTitleText>{t('Overview')}</TabTitleText>}
                    aria-label={t(
                      'Course overview containing detailed information',
                    )}
                  >
                    {progress?.total_progress > 0 && (
                      <LandingLayout.Progress>
                        {t('Progress: ')}
                        <strong>{formattedProgress} %</strong>
                      </LandingLayout.Progress>
                    )}
                    <>
                      <Description description={description} />
                      <Prerequisites prerequisites={prerequisites} />
                    </>
                    <AvailableLanguages translations={translations} />
                  </Tab>
                </LandingLayout.Tabs>
              </div>
              <div className="overview__sidebar">
                {techOverviewSlug !== '' && (
                  <Link
                    href
                    routeName="tech-overview:view"
                    routeParams={{ slug: techOverviewSlug }}
                  >
                    <LandingLayout.SidebarButton isBlock>
                      {launchButtonLabel}
                    </LandingLayout.SidebarButton>
                  </Link>
                )}

                {versions?.length > 0 && (
                  <VersionsDropdown
                    code={code}
                    currentVersion={currentVersion}
                    onOptionSelect={handleSelect}
                    versions={versions}
                    displayTooltip={false}
                  />
                )}

                <ExploreSubscriptions />

                <SidebarDetails
                  productLabels={productLabels}
                  audienceLabels={audienceLabels}
                  topicLabels={topicLabels}
                  language="en-US"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default observer(TechOverviewOverview);
